/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { take, map } from 'rxjs/operators';

export interface ActiveLineValidatorData {
	activeLineDate?: string;
	activeLineX?: number;
}

interface ActiveLineValidatorConfigBase {
	message: string;
	messageHeader: string;
}

interface ActiveLineValidatorConfigSimple extends ActiveLineValidatorConfigBase {
	comparisonMethod: 'date' | 'x';
	comparisonStyle: '>' | '<' | '<=' | '>='; //I miss languages where you can assign operators to variables :(
}

interface ActiveLineValidatorConfigCustom extends ActiveLineValidatorConfigBase {
	comparisonFunction: (lineData: ActiveLineValidatorData, controlValue) => boolean;
}

export type ActiveLineValidatorConfig = ActiveLineValidatorConfigSimple | ActiveLineValidatorConfigCustom;

export function activeLineSideValidator(
	activeLine$: Observable<ActiveLineValidatorData>,
	config: ActiveLineValidatorConfig
) {
	return (control: UntypedFormControl) => {
		return activeLine$.pipe(
			take(1),
			map((line) => {
				if (!control.value || control.value === '') {
					return null;
				} //handle via required validator if you don't want null
				if (!line) {
					return null;
				} //null is valid, maybe enhance config if different behavior desired

				let relevantLineData;
				let isValid;
				if ('comparisonFunction' in config) {
					isValid = config.comparisonFunction(line, control.value);
				} else {
					if (config.comparisonMethod === 'date') {
						relevantLineData = line.activeLineDate;
					} else if (config.comparisonMethod === 'x') {
						relevantLineData = line.activeLineX;
					} else {
						throw new Error('provided config is invalid');
					}

					if (config.comparisonStyle === '>') {
						isValid = control.value > relevantLineData;
					} else if (config.comparisonStyle === '<') {
						isValid = control.value < relevantLineData;
					} else if (config.comparisonStyle === '<=') {
						isValid = control.value <= relevantLineData;
					} else if (config.comparisonStyle === '>=') {
						isValid = control.value >= relevantLineData;
					} else {
						throw new Error('provided config is invalid');
					}
				}

				return isValid
					? null
					: {
							activeLineSideValidator: {
								value: control.value,
								message: config.message,
								messageHeader: config.messageHeader,
							},
					  };
			})
		);
	};
}
